import { Check, Storefront, Toolbox } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useExperience, type Experience } from '@/hooks/use-experience';
import { HOME_PATH, OPERATION_PATH } from '../utils/slugs';

/**
 * Escolha de experiência do admin.
 *
 * **Ela escolhe para onde ir, não o que é permitido.** A vitrine continua
 * aberta a qualquer papel de plataforma nos dois modos — um employee que receba
 * o link de um produto o abre. Fechar a porta produziria 404 numa URL que a
 * pessoa tem permissão de ver e que alguém do time mandou para ela.
 *
 * Renderiza `null` para quem não alterna (buyer, employee) e sem sessão.
 */
export function ExperienceToggle() {
  const { experience, canToggle, setExperience } = useExperience();
  const navigate = useNavigate();

  if (!canToggle || experience === null) return null;

  /**
   * Escolher a experiência agora NAVEGA, e não só persiste.
   *
   * Antes quem levava era o `RoleLanding` na raiz: trocar a preferência e
   * voltar para `/` caía na tela certa. Com a vitrine ocupando a raiz aquele
   * componente sumiu, e sem isto o toggle passaria a não fazer nada visível.
   */
  const escolher = (valor: Experience) => {
    setExperience(valor);
    navigate(valor === 'storefront' ? HOME_PATH : OPERATION_PATH);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Escolher experiência">
          {experience === 'storefront' ? (
            <Storefront size={18} aria-hidden />
          ) : (
            <Toolbox size={18} aria-hidden />
          )}
          <span className="sr-only">
            {experience === 'storefront'
              ? 'Vitrine é a sua tela inicial'
              : 'Operação é a sua tela inicial'}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Tela inicial</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <Option
          value="storefront"
          current={experience}
          onSelect={escolher}
          icon={<Storefront size={16} aria-hidden />}
          label="Vitrine"
        />
        <Option
          value="self-service"
          current={experience}
          onSelect={escolher}
          icon={<Toolbox size={16} aria-hidden />}
          label="Operação"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Option({
  value,
  current,
  onSelect,
  icon,
  label,
}: {
  value: Experience;
  current: Experience;
  onSelect: (value: Experience) => void;
  icon: React.ReactNode;
  label: string;
}) {
  const active = current === value;
  return (
    <DropdownMenuItem onClick={() => onSelect(value)} aria-current={active}>
      {icon}
      <span className="flex-1">{label}</span>
      {active && <Check size={16} aria-hidden />}
    </DropdownMenuItem>
  );
}
